import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";

/**
 * Product Search component
 * Displays search and sort controls for product catalog
 *
 * @param {Object} props - Component props
 * @param {string} props.searchTerm - Current search term
 * @param {Function} props.handleSearchChange - Function to handle search change
 * @param {string} props.sortBy - Current sort option
 * @param {Function} props.handleSortChange - Function to handle sort change
 * @param {Function} props.toggleFilterDrawer - Function to toggle filter drawer (mobile)
 * @returns {JSX.Element} The ProductSearch component
 */
const ProductSearch = ({
  searchTerm,
  handleSearchChange,
  sortBy,
  handleSortChange,
  toggleFilterDrawer,
}) => {
  const { t: tProducts } = useTranslation("products");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 2,
        mb: 3,
        alignItems: "center",
      }}
    >
      <TextField
        placeholder={tProducts("catalog.search.placeholder")}
        value={searchTerm}
        onChange={handleSearchChange}
        variant="outlined"
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ flex: 1 }}
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="sort-select-label">
            {tProducts("catalog.sort.label")}
          </InputLabel>
          <Select
            labelId="sort-select-label"
            id="sort-select"
            value={sortBy}
            label={tProducts("catalog.sort.label")}
            onChange={handleSortChange}
          >
            <MenuItem value="newest">
              {tProducts("catalog.sort.options.newest")}
            </MenuItem>
            <MenuItem value="price-low">
              {tProducts("catalog.sort.options.priceAsc")}
            </MenuItem>
            <MenuItem value="price-high">
              {tProducts("catalog.sort.options.priceDesc")}
            </MenuItem>
          </Select>
        </FormControl>

        {isMobile && (
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={toggleFilterDrawer}
            sx={{ whiteSpace: "nowrap" }}
          >
            {tProducts("catalog.mobileFilters.show")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ProductSearch;
